// ============================================
// FILE: src/pages/DocumentsPage.jsx
// ============================================
import React, { useState, useEffect, useRef } from 'react';
import { FolderOpen, FileCheck, Shield, CheckCircle2, Download, Eye, Lock, Sparkles, ArrowRight, BadgeCheck } from 'lucide-react';
import { documents } from '../data/properties';

/* ─── Global keyframes injected once ─── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@400;500;600;700&display=swap');

  @keyframes floatA {
    0%,100%{transform:translateY(0) rotate(0deg);}
    50%{transform:translateY(-14px) rotate(2deg);}
  }
  @keyframes floatB {
    0%,100%{transform:translateY(0) rotate(0deg);}
    50%{transform:translateY(-10px) rotate(-3deg);}
  }
  @keyframes revealUp {
    from{opacity:0;transform:translateY(40px);}
    to{opacity:1;transform:translateY(0);}
  }
  @keyframes shimmerMove {
    0%{background-position:-300% center;}
    100%{background-position:300% center;}
  }
  @keyframes scanLine {
    0%{top:0%;}100%{top:100%;}
  }
  @keyframes badgePop {
    0%{transform:scale(0.7);opacity:0;}
    70%{transform:scale(1.15);}
    100%{transform:scale(1);opacity:1;}
  }
  @keyframes pulseGlow {
    0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0.35);}
    50%{box-shadow:0 0 0 10px rgba(16,185,129,0);}
  }
  @keyframes gridScroll {
    from{transform:translateY(0);}
    to{transform:translateY(-50%);}
  }
  @keyframes rotateSlow {
    from{transform:rotate(0deg);}
    to{transform:rotate(360deg);}
  }

  .doc-page * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }
  .doc-page h1, .doc-page h2 { font-family: 'DM Serif Display', serif; }

  .doc-card {
    position:relative; background:#fff; border-radius:20px;
    border:1.5px solid #e5e7eb;
    overflow:hidden; cursor:pointer;
    transition: transform 0.35s cubic-bezier(.34,1.56,.64,1),
                border-color 0.3s, box-shadow 0.3s;
  }
  .doc-card:hover {
    transform:translateY(-6px) scale(1.015);
    box-shadow:0 20px 48px rgba(0,0,0,0.1);
  }
  .doc-card::before {
    content:''; position:absolute; inset:0; opacity:0;
    background:linear-gradient(135deg,rgba(16,185,129,0.04),rgba(5,150,105,0.02));
    transition:opacity 0.3s;
    pointer-events:none;
  }
  .doc-card:hover::before { opacity:1; }

  .scan-overlay {
    position:absolute; left:0; right:0; height:3px;
    background:linear-gradient(90deg,transparent,rgba(16,185,129,0.6),transparent);
    pointer-events:none; opacity:0;
    animation:scanLine 1.6s linear infinite;
  }
  .doc-card:hover .scan-overlay { opacity:1; }

  .verify-badge {
    opacity:0; transform:scale(0.7);
    transition:none;
  }
  .doc-card:hover .verify-badge {
    animation:badgePop 0.4s ease forwards;
  }

  .action-btn {
    display:inline-flex; align-items:center; gap:6px;
    padding:8px 14px; border-radius:10px; font-size:13px;
    font-weight:600; border:none; cursor:pointer;
    transition:all 0.25s ease;
  }
  .action-btn.primary {
    background:linear-gradient(135deg,#059669,#10b981);
    color:#fff;
  }
  .action-btn.primary:hover {
    background:linear-gradient(135deg,#047857,#059669);
    box-shadow:0 6px 20px rgba(5,150,105,0.35);
    transform:translateY(-1px);
  }
  .action-btn.ghost {
    background:#f0fdf4; color:#059669;
  }
  .action-btn.ghost:hover {
    background:#dcfce7; transform:translateY(-1px);
  }
`;

/* ─── IntersectionObserver hook ─── */
function useVisible(threshold = 0.12) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

/* ─── Accent color per card index ─── */
const ACCENTS = [
  { bg: '#ecfdf5', border: '#6ee7b7', icon: '#059669', badge: '#d1fae5', badgeText: '#065f46' },
  { bg: '#eff6ff', border: '#93c5fd', icon: '#2563eb', badge: '#dbeafe', badgeText: '#1e40af' },
  { bg: '#fefce8', border: '#fde68a', icon: '#d97706', badge: '#fef3c7', badgeText: '#92400e' },
  { bg: '#fdf4ff', border: '#e9d5ff', icon: '#9333ea', badge: '#f3e8ff', badgeText: '#6b21a8' },
  { bg: '#fff1f2', border: '#fecdd3', icon: '#e11d48', badge: '#ffe4e6', badgeText: '#9f1239' },
  { bg: '#f0fdfa', border: '#99f6e4', icon: '#0d9488', badge: '#ccfbf1', badgeText: '#134e4a' },
  { bg: '#fff7ed', border: '#fed7aa', icon: '#ea580c', badge: '#ffedd5', badgeText: '#7c2d12' },
  { bg: '#f0f9ff', border: '#bae6fd', icon: '#0284c7', badge: '#e0f2fe', badgeText: '#075985' },
];

/* ─── Single document card ─── */
const DocCard = ({ doc, index }) => {
  const [ref, vis] = useVisible();
  const [downloaded, setDownloaded] = useState(false);
  const a = ACCENTS[index % ACCENTS.length];

  return (
    <div
      ref={ref}
      className="doc-card"
      style={{
        opacity: vis ? 1 : 0,
        transform: vis ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.5s ease ${index * 0.07}s, transform 0.5s ease ${index * 0.07}s, border-color 0.3s, box-shadow 0.3s`,
        borderColor: a.border,
      }}
    >
      {/* Scan-line shimmer on hover */}
      <div className="scan-overlay" />

      {/* Left accent bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: a.icon, borderRadius: '20px 0 0 20px' }} />

      <div style={{ padding: '20px 20px 20px 24px', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        {/* Icon bubble */}
        <div style={{
          minWidth: '52px', height: '52px', borderRadius: '14px',
          background: a.bg, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '26px',
          border: `1px solid ${a.border}`,
          transition: 'transform 0.3s ease',
        }}>
          {doc.icon}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', margin: 0 }}>{doc.title}</h3>
            <span className="verify-badge" style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              background: a.badge, color: a.badgeText,
              fontSize: '11px', fontWeight: '700', borderRadius: '6px', padding: '2px 7px',
            }}>
              <BadgeCheck size={11} /> Verified
            </span>
          </div>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 14px', lineHeight: 1.5 }}>{doc.description}</p>

          {/* Action buttons */}
          {/* <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className="action-btn ghost"
              onClick={() => {}}
            >
              <Eye size={13} /> Preview
            </button>
            <button
              className="action-btn primary"
              onClick={() => setDownloaded(true)}
              style={downloaded ? { background: '#065f46' } : {}}
            >
              {downloaded ? <CheckCircle2 size={13} /> : <Download size={13} />}
              {downloaded ? 'Saved!' : 'Download'}
            </button>
          </div> */}
        </div>

        {/* Status badge */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minWidth: '44px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: '#d1fae5', display: 'flex', alignItems: 'center',
            justifyContent: 'center', animation: 'pulseGlow 2.5s ease infinite',
          }}>
            <FileCheck size={16} color="#059669" />
          </div>
          <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.05em' }}>READY</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Scrolling grid background ─── */
const GridBg = () => (
  <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden', opacity: 0.35 }}>
    <div style={{
      position: 'absolute', inset: 0,
      backgroundImage: `
        linear-gradient(rgba(16,185,129,0.07) 1px, transparent 1px),
        linear-gradient(90deg, rgba(16,185,129,0.07) 1px, transparent 1px)
      `,
      backgroundSize: '40px 40px',
      animation: 'gridScroll 25s linear infinite',
    }} />
  </div>
);

/* ─── Main page ─── */
const DocumentsPage = () => {
  const [heroRef, heroVis] = useVisible(0.1);

  return (
    <div className="doc-page" style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#f9fafb 0%,#ffffff 50%,#f0fdf4 100%)', position: 'relative' }}>
      <style>{STYLES}</style>
      <GridBg />

      {/* Ambient blobs */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: '-100px', right: '-80px', width: '480px', height: '480px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(16,185,129,0.1) 0%,transparent 70%)', animation: 'floatA 8s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '5%', left: '-80px', width: '380px', height: '380px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(5,150,105,0.07) 0%,transparent 70%)', animation: 'floatB 11s ease-in-out infinite 1.5s' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: '1040px', margin: '0 auto', padding: '60px 24px 80px' }}>

        {/* ── HERO ── */}
        <div ref={heroRef} style={{ textAlign: 'center', marginBottom: '60px' }}>

          {/* Rotating ring decoration */}
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '24px' }}>
            <div style={{
              position: 'absolute', inset: '-12px', borderRadius: '50%',
              border: '1.5px dashed #6ee7b7',
              animation: 'rotateSlow 12s linear infinite',
            }} />
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'linear-gradient(135deg,#059669,#10b981)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 12px 32px rgba(5,150,105,0.35)',
              animation: 'pulseGlow 2.5s ease infinite',
            }}>
              <FolderOpen size={34} color="#fff" strokeWidth={1.8} />
            </div>
          </div>

          {/* Sparkle badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            background: 'linear-gradient(90deg,#ecfdf5,#d1fae5,#ecfdf5)',
            backgroundSize: '200% auto',
            animation: 'shimmerMove 3s linear infinite',
            border: '1px solid #a7f3d0', borderRadius: '999px',
            padding: '5px 16px', marginBottom: '22px', display: 'flex',
            justifyContent: 'center', width: 'fit-content', margin: '0 auto 22px',
          }}>
            <Sparkles size={13} color="#059669" />
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#065f46', letterSpacing: '0.07em' }}>100% GENUINE VERIFICATION</span>
          </div>

          <h1 style={{
            fontSize: 'clamp(38px,6vw,68px)', color: '#064e3b', lineHeight: 1.08,
            margin: '0 0 18px', letterSpacing: '-0.02em',
            opacity: heroVis ? 1 : 0,
            transform: heroVis ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}>
            Property <em style={{ color: '#059669', fontStyle: 'italic' }}>Documents</em>
          </h1>

          <p style={{
            fontSize: '17px', color: '#6b7280', maxWidth: '500px', margin: '0 auto 0',
            lineHeight: 1.7,
            opacity: heroVis ? 1 : 0,
            transform: heroVis ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease 0.15s, transform 0.6s ease 0.15s',
          }}>
            Complete documentation support for hassle-free, legally compliant property transactions — all in one place.
          </p>
        </div>

        {/* ── Stats strip ── */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '0',
          background: '#fff', border: '1.5px solid #e5e7eb',
          borderRadius: '18px', padding: '0', marginBottom: '48px',
          overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
          flexWrap: 'wrap',
        }}>
          {[
            { val: documents.length, label: 'Document Types', icon: '📁' },
            { val: '24h', label: 'Processing Time', icon: '⚡' },
            { val: '100%', label: 'Legal Compliance', icon: '⚖️' },
            { val: '500+', label: 'Clients Served', icon: '🏠' },
          ].map((s, i) => (
            <div key={i} style={{
              flex: '1', minWidth: '120px', textAlign: 'center',
              padding: '20px 16px',
              borderRight: i < 3 ? '1px solid #f3f4f6' : 'none',
            }}>
              <div style={{ fontSize: '22px', marginBottom: '4px' }}>{s.icon}</div>
              <div style={{ fontSize: '22px', fontWeight: '800', color: '#059669', marginBottom: '2px' }}>{s.val}</div>
              <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '600' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Documents grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(440px,1fr))', gap: '20px', marginBottom: '48px' }}>
          {documents.map((doc, i) => (
            <DocCard key={doc.id} doc={doc} index={i} />
          ))}
        </div>

        {/* ── Trust banner ── */}
        <div style={{
          borderRadius: '24px',
          background: 'linear-gradient(135deg,#064e3b 0%,#065f46 40%,#047857 100%)',
          padding: '40px 36px',
          display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap',
          position: 'relative', overflow: 'hidden',
          boxShadow: '0 20px 56px rgba(5,150,105,0.28)',
        }}>
          {/* Decorative rings */}
          <div style={{ position: 'absolute', right: '-60px', top: '-60px', width: '240px', height: '240px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'absolute', right: '20px', bottom: '-40px', width: '160px', height: '160px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.05)' }} />

          <div style={{
            width: '60px', height: '60px', borderRadius: '16px',
            background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)',
            animation: 'pulseGlow 2.5s ease infinite',
          }}>
            <Shield size={28} color="#6ee7b7" />
          </div>

          <div style={{ flex: 1, minWidth: '200px' }}>
            <h2 style={{ fontSize: '20px', color: '#fff', margin: '0 0 6px' }}>
              Shree Sai Properties Guarantee
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', margin: 0, fontSize: '14px', lineHeight: 1.6 }}>
              We ensure 100% genuine document verification and full legal compliance — every file is cross-checked by our certified legal team before delivery.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexShrink: 0, flexWrap: 'wrap' }}>
            {['Rera Verified', 'Court Valid', 'Govt Approved'].map(tag => (
              <span key={tag} style={{
                background: 'rgba(255,255,255,0.12)', color: '#a7f3d0',
                border: '1px solid rgba(110,231,183,0.3)',
                borderRadius: '8px', padding: '6px 14px',
                fontSize: '13px', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '5px',
              }}>
                <CheckCircle2 size={13} /> {tag}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default DocumentsPage;